import {
  defaultBorders,
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
  golfstatusColors,
  GSButton,
  GSInfoGroup,
  GSItemList,
} from "golfstatus_react_components";
import { faArrowsRotate, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { getDateTime, getV3DateTime } from "../../../helpers/Dates";
import { RECIPIENT_GROUP_LABELS } from "../../../forms/MessageForms";
import TournamentMessageConfirmationTournamentItem from "./tournament-message-confirmation-tournament-item";

const GROUP_ORDER = Object.keys(RECIPIENT_GROUP_LABELS);

const TournamentMessageConfirmationItem = (props) => {
  const {
    currentMessage = {},
    tournaments = [],
    recipientGroups = [],
    organizationName,
    refreshing = false,
    onRefresh,
    showRefresh = false,
  } = props;

  const {
    subject,
    status,
    scheduledFor,
    sender,
    channel,
    uniqueSelectedCount = 0,
    recipientsRefreshedAt,
  } = currentMessage;

  const messageType = channel === "email" ? "email" : "app notification";

  const getGroupNames = (tournament) =>
    GROUP_ORDER.filter((groupType) =>
      recipientGroups.some(
        (g) =>
          g?.tournamentId === tournament?.id &&
          g?.groupType === groupType &&
          (g?.includeAll === true || (g?.selectedCount ?? 0) > 0)
      )
    ).map((groupType) => RECIPIENT_GROUP_LABELS[groupType]);

  const senderName = `${sender?.firstName ?? ""} ${sender?.lastName ?? ""}`.trim();

  const headerTopItems = [
    {
      type: "full-width",
      sectionItemStyle: { ...defaultTypography.headline5 },
      value: subject || "No Subject",
    },
  ];
  if (organizationName) {
    headerTopItems.push({
      type: "secondary full-width",
      value: `Sending from ${organizationName}`,
    });
  }

  const headerBottomItems = [];
  if (status === "scheduled" && scheduledFor) {
    headerBottomItems.push({
      type: "secondary full-width",
      value: `Sending on ${getV3DateTime(scheduledFor)}`,
    });
  }
  if (senderName) {
    headerBottomItems.push({
      type: "secondary full-width",
      value: `Created by ${senderName}`,
    });
  }

  const totalGroups = [
    {
      fit: "stretch",
      sections: [
        {
          gap: "small-gap",
          sectionItems: [
            {
              type: "primary full-width",
              value: (
                <span
                  style={{
                    ...defaultTypography.headline1,
                    fontSize: 36,
                    color: golfstatusColors.cyan700,
                  }}
                >
                  {uniqueSelectedCount}
                </span>
              ),
            },
            {
              type: "primary full-width",
              value: `${uniqueSelectedCount === 1 ? "Person" : "People"} Will Receive This Message`,
            },
          ],
        },
      ],
    },
  ];
  if (showRefresh) {
    totalGroups.push({
      sections: [
        {
          sectionItems: [
            {
              type: "primary no-wrap",
              value: (
                <GSButton
                  title="Refresh"
                  buttonIcon={refreshing ? faSpinner : faArrowsRotate}
                  type="light-grey no-wrap mobile-icon"
                  isDisabled={refreshing}
                  onClick={onRefresh}
                />
              ),
            },
          ],
        },
      ],
    });
  }

  const excludedTournaments = tournaments.filter((t) => t?.excluded === true);
  const breakdownTournaments = tournaments.filter(
    (t) => t?.excluded !== true && ((t?.uniqueRecipientCount ?? 0) > 0 || getGroupNames(t).length > 0)
  );

  return (
    <tournament-message-confirmation-item style={{ display: "block", width: "100%" }}>
      <div style={{ ...defaultLayouts.vStack, ...defaultLayouts.fullWidth, ...defaultGapping.xLargeGap }}>
        <div style={{ ...defaultLayouts.vStack, ...defaultLayouts.fullWidth, ...defaultGapping.largeGap }}>
          <GSInfoGroup
            listType="mobile-vertical"
            style={{ whiteSpace: "normal" }}
            dataGroups={[{ fit: "stretch", sections: [{ sectionItems: headerTopItems }] }]}
          />
          {headerBottomItems.length > 0 && (
            <GSInfoGroup
              listType="mobile-vertical"
              style={{ whiteSpace: "normal" }}
              dataGroups={[{ fit: "stretch", sections: [{ sectionItems: headerBottomItems }] }]}
            />
          )}
          <div
            style={{
              ...defaultLayouts.fullWidth,
              borderBottom: `1px solid ${golfstatusColors.grey200}`,
            }}
          />
        </div>

        <div style={{ ...defaultLayouts.vStack, ...defaultLayouts.fullWidth, ...defaultGapping.largeGap }}>
          <GSInfoGroup dataGroups={totalGroups} />
          <GSInfoGroup
            listType="mobile-vertical"
            style={{ whiteSpace: "normal" }}
            dataGroups={[
              {
                fit: "stretch",
                sections: [
                  {
                    sectionItems: [
                      ...(status !== "sent" && (refreshing || recipientsRefreshedAt)
                        ? [
                            {
                              type: "secondary full-width",
                              value: `Approximate Recipient Count – ${
                                refreshing
                                  ? "Updating..."
                                  : `Updated on ${getDateTime(recipientsRefreshedAt)}`
                              }`,
                            },
                          ]
                        : []),
                      ...(showRefresh
                        ? [
                            {
                              type: "tertiary full-width",
                              value:
                                "Recipients are refreshed automatically at the time of send, ensuring delivery to the most current list.",
                            },
                          ]
                        : []),
                    ],
                  },
                ],
              },
            ]}
          />
        </div>

        {excludedTournaments.map((tournament) => (
          <div
            key={tournament.id}
            style={{
              ...defaultLayouts.fullWidth,
              ...defaultPadding.xLargePad,
              ...defaultBorders.mediumLargeBorderRadius,
              backgroundColor: golfstatusColors.grey50,
              boxSizing: "border-box",
              whiteSpace: "normal",
            }}
          >
            <GSInfoGroup
              dataGroups={[
                {
                  fit: "stretch",
                  sections: [
                    {
                      sectionItems: [
                        {
                          type: "primary full-width",
                          sectionItemStyle: { whiteSpace: "normal" },
                          value: tournament?.name ?? "Tournament",
                        },
                        {
                          type: "secondary full-width",
                          sectionItemStyle: { whiteSpace: "normal" },
                          value: "Registered Participants Excluded",
                        },
                      ],
                    },
                  ],
                },
              ]}
            />
          </div>
        ))}

        {breakdownTournaments.length > 0 && (
          <GSItemList
            type="vertical large-gap"
            items={breakdownTournaments}
            listItem={(tournament) => (
              <TournamentMessageConfirmationTournamentItem
                tournament={tournament}
                groupNames={getGroupNames(tournament)}
                messageType={messageType}
              />
            )}
          />
        )}
      </div>
    </tournament-message-confirmation-item>
  );
};

export default TournamentMessageConfirmationItem;
